import { ReloadOutlined } from '@ant-design/icons';
import type { SupplierRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatDisplayDate } from '@hq/utils';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Descriptions, Drawer, Input, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { hq } from '../api/client';
import { ellipsisCell, sumWidth } from '@hq/ui-kit';

interface SupplierQualLocal {
  id: string;
  qualType: string;
  qualNo: string | null;
  expireAt: string | null;
  issueAt: string | null;
  status: string;
}

const COOPERATE_META: Record<string, { text: string; color: string }> = {
  ACTIVE: { text: '合作中', color: 'green' },
  PENDING: { text: '待准入', color: 'gold' },
  TRIAL: { text: '试用', color: 'cyan' },
  SUSPENDED: { text: '暂停', color: 'orange' },
  STOPPED: { text: '暂停', color: 'orange' },
  BLACKLIST: { text: '黑名单', color: 'red' },
  TERMINATED: { text: '已终止', color: 'default' }
};

/** 供应商档案（医院端只读）：供货物资 + 资质证照，维护入口在运营端「供应商协同」。 */
export default function BasicSupplier() {
  const [keyword, setKeyword] = useState('');
  const [current, setCurrent] = useState<SupplierRow | null>(null);

  const list = useQuery({
    queryKey: ['basic', 'supplier', keyword],
    queryFn: () => hq.catalog.suppliers({ keyword: keyword || undefined, size: 100 })
  });

  const materials = useQuery({
    queryKey: ['basic', 'supplier', 'materials', current?.id],
    enabled: Boolean(current),
    queryFn: () => hq.catalog.supplierMaterials(current?.id ?? '')
  });

  const quals = useQuery({
    queryKey: ['basic', 'supplier', 'quals', current?.id],
    enabled: Boolean(current),
    queryFn: () => hq.catalog.quals(current?.id ?? '')
  });

  const columns: ColumnsType<SupplierRow> = [
    { title: '编码', dataIndex: 'supplierCode', width: 120 },
    { title: '供应商名称', dataIndex: 'supplierName', width: 240, ellipsis: { showTitle: false }, render: ellipsisCell('supplierName') },
    {
      title: '类型',
      dataIndex: 'supplierType',
      width: 110,
      render: (value: string | null) => (
        <Tag>{value === 'OPERATOR' ? '运营方' : value === 'MANUFACTURER' ? '生产厂家' : '第三方'}</Tag>
      )
    },
    {
      title: '合作状态',
      dataIndex: 'cooperateStatus',
      width: 110,
      render: (value: string | null) => {
        const meta = COOPERATE_META[value ?? ''] ?? { text: value ?? '-', color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    { title: '联系人', dataIndex: 'contactPerson', width: 110, render: (value: string | null) => value || '-' },
    { title: '联系电话', dataIndex: 'contactPhone', width: 140, render: (value: string | null) => value || '-' },
    { title: '评分', dataIndex: 'score', width: 80, align: 'right' },
    {
      title: '操作',
      width: 90,
      render: (_, row) => (
        <Button type="link" onClick={() => setCurrent(row)}>
          档案
        </Button>
      )
    }
  ];

  return (
    <Card
      className="page-card"
      title={<Space>供应商档案<Typography.Text type="secondary">只读；准入与资质维护在运营端</Typography.Text></Space>}
      extra={
        <Space>
          <Input.Search
            allowClear
            placeholder="编码 / 名称"
            style={{ width: 220 }}
            onSearch={(value) => setKeyword(value.trim())}
          />
          <Button icon={<ReloadOutlined />} loading={list.isFetching} onClick={() => list.refetch()}>
            刷新
          </Button>
        </Space>
      }
    >
      {list.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="供应商加载失败"
          description={list.error instanceof ApiError ? list.error.friendlyMessage : String(list.error)}
        />
      ) : null}
      <Table<SupplierRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={list.data?.items ?? []}
        loading={list.isPending}
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
        scroll={{ x: sumWidth(columns) }}
      />

      <Drawer title={current?.supplierName ?? '供应商档案'} width={860} open={Boolean(current)} onClose={() => setCurrent(null)}>
        {current ? (
          <>
            <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="编码">{current.supplierCode}</Descriptions.Item>
              <Descriptions.Item label="状态">{COOPERATE_META[current.cooperateStatus ?? '']?.text ?? current.cooperateStatus}</Descriptions.Item>
              <Descriptions.Item label="统一社会信用代码">{current.creditCode || '-'}</Descriptions.Item>
              <Descriptions.Item label="法人">{current.legalPerson || '-'}</Descriptions.Item>
              <Descriptions.Item label="联系人">{current.contactPerson || '-'}</Descriptions.Item>
              <Descriptions.Item label="电话">{current.contactPhone || '-'}</Descriptions.Item>
              <Descriptions.Item label="地址" span={2}>{current.address || '-'}</Descriptions.Item>
            </Descriptions>

            <Typography.Title level={5}>资质证照</Typography.Title>
            <Table<SupplierQualLocal>
              rowKey="id"
              size="small"
              pagination={false}
              loading={Boolean(current) && quals.isPending}
              dataSource={(quals.data ?? []) as SupplierQualLocal[]}
              locale={{ emptyText: '暂无资质记录' }}
              columns={[
                { title: '资质类型', dataIndex: 'qualType', width: 160 },
                { title: '证书编号', dataIndex: 'qualNo', width: 180, render: (v: string | null) => v || '-' },
                { title: '发证日', dataIndex: 'issueAt', width: 120, render: (v: string | null) => formatDisplayDate(v) },
                { title: '到期日', dataIndex: 'expireAt', width: 120, render: (v: string | null) => formatDisplayDate(v) },
                {
                  title: '状态',
                  dataIndex: 'status',
                  width: 100,
                  render: (v: string) => <Tag color={v === 'VALID' ? 'green' : v === 'EXPIRING' ? 'orange' : 'red'}>{v}</Tag>
                }
              ]}
            />

            <Typography.Title level={5} style={{ marginTop: 16 }}>供货物资</Typography.Title>
            <Table
              rowKey="id"
              size="small"
              pagination={false}
              loading={Boolean(current) && materials.isPending}
              dataSource={materials.data?.items ?? []}
              locale={{ emptyText: '尚未备案供货物资（运营端「供应商协同」维护）' }}
              columns={[
                { title: '物资编码', dataIndex: 'materialCode', width: 130, render: (v: string | null) => v || '-' },
                { title: '物资名称', dataIndex: 'materialName', width: 200, render: (v: string | null) => v || '-' },
                { title: '供应商 SKU', dataIndex: 'supplierSkuCode', width: 150, render: (v: string | null) => v || '-' },
                { title: '条码', dataIndex: 'supplierBarcode', width: 150, render: (v: string | null) => v || '-' },
                { title: '交期(h)', dataIndex: 'leadTimeHours', width: 90, align: 'right' },
                { title: '起订量', dataIndex: 'moq', width: 90, align: 'right' }
              ]}
            />
          </>
        ) : null}
      </Drawer>
    </Card>
  );
}
